import { isSuppressedUrlString } from "@/lib/analytics/suppressed-paths";

export const SIGNUP_ATTRIBUTION_STORAGE_KEY = "tsb_attribution";
export const SIGNUP_ATTRIBUTION_METADATA_KEY = "tsbAttribution";
export const SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY = "tsb_last_attribution";
export const SIGNUP_LAST_ATTRIBUTION_METADATA_KEY = "tsbLastAttribution";

const MAX_ATTRIBUTION_VALUE_LENGTH = 500;

export const SIGNUP_ATTRIBUTION_FIELDS = [
  { key: "utmSource", queryParam: "utm_source", slackName: "UTM source", inline: true },
  { key: "utmMedium", queryParam: "utm_medium", slackName: "UTM medium", inline: true },
  { key: "utmCampaign", queryParam: "utm_campaign", slackName: "UTM campaign", inline: true },
  { key: "utmContent", queryParam: "utm_content", slackName: "UTM content", inline: true },
  { key: "utmTerm", queryParam: "utm_term", slackName: "UTM term", inline: true },
  { key: "utmOffer", queryParam: "utm_offer", slackName: "UTM offer", inline: true },
  { key: "ref", queryParam: "ref", slackName: "Referral code", inline: true },
  { key: "landingPage", slackName: "Landing page", inline: false },
  { key: "referrer", slackName: "Referrer", inline: false },
  { key: "gclid", queryParam: "gclid", slackName: "Google click ID", inline: false },
  { key: "gbraid", queryParam: "gbraid", slackName: "GBRAID", inline: false },
  { key: "wbraid", queryParam: "wbraid", slackName: "WBRAID", inline: false },
  { key: "msclkid", queryParam: "msclkid", slackName: "Microsoft click ID", inline: false },
  { key: "gadSource", queryParam: "gad_source", slackName: "Google Ads source", inline: true },
  { key: "gadCampaignId", queryParam: "gad_campaignid", slackName: "Google Ads campaign ID", inline: true },
  { key: "capturedAt", slackName: "Captured at", inline: false },
  { key: "sourcePath", slackName: "Source path", inline: false },
  { key: "visitorId", slackName: "Visitor ID", inline: false },
] as const;

export type SignupAttributionKey = (typeof SIGNUP_ATTRIBUTION_FIELDS)[number]["key"];
export type SignupAttribution = Partial<Record<SignupAttributionKey, string>>;

export type SlackField = { name: string; value: string; inline?: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Attribution fields that hold a URL and could therefore carry a bearer token.
// Dropped defensively during normalisation if token-bearing, so a client that
// bypassed the source-side scrub can't surface the token to Clerk/Slack.
const URL_ATTRIBUTION_KEYS = new Set(["landingPage", "sourcePath", "referrer"]);

function sanitizeAttributionValue(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.length <= MAX_ATTRIBUTION_VALUE_LENGTH) return trimmed;
  return `${trimmed.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH - 3)}...`;
}

function extractAttributionRecord(
  input: unknown,
  metadataKey = SIGNUP_ATTRIBUTION_METADATA_KEY,
): Record<string, unknown> | null {
  if (!isRecord(input)) return null;

  const nested = input[metadataKey];
  if (isRecord(nested)) return nested;

  return input;
}

export function normalizeSignupAttribution(input: unknown): SignupAttribution | null {
  const record = extractAttributionRecord(input);
  if (!record) return null;

  const attribution: SignupAttribution = {};
  for (const field of SIGNUP_ATTRIBUTION_FIELDS) {
    const value = sanitizeAttributionValue(record[field.key]);
    if (!value) continue;
    if (URL_ATTRIBUTION_KEYS.has(field.key) && isSuppressedUrlString(value)) {
      continue;
    }
    attribution[field.key] = value;
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

export function normalizeLastSignupAttribution(input: unknown): SignupAttribution | null {
  const record = extractAttributionRecord(input, SIGNUP_LAST_ATTRIBUTION_METADATA_KEY);
  if (!record) return null;

  const attribution: SignupAttribution = {};
  for (const field of SIGNUP_ATTRIBUTION_FIELDS) {
    const value = sanitizeAttributionValue(record[field.key]);
    if (!value) continue;
    if (URL_ATTRIBUTION_KEYS.has(field.key) && isSuppressedUrlString(value)) {
      continue;
    }
    attribution[field.key] = value;
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

export function buildSignupAttributionFromSearch(
  searchParams: URLSearchParams,
  referrer: string | null,
  landingPage: string,
  sourcePath?: string,
  capturedAt?: string,
  visitorId?: string,
): SignupAttribution | null {
  const candidate: Record<string, string | null> = {
    referrer,
    landingPage,
    sourcePath: sourcePath ?? landingPage.split("?")[0] ?? landingPage,
    capturedAt: capturedAt ?? null,
    visitorId: visitorId ?? null,
  };

  for (const field of SIGNUP_ATTRIBUTION_FIELDS) {
    if (!("queryParam" in field)) continue;
    candidate[field.key] = searchParams.get(field.queryParam);
  }

  return normalizeSignupAttribution(candidate);
}

export function buildSignupAttributionSlackFields(input: unknown): SlackField[] {
  const attribution = normalizeSignupAttribution(input);
  if (!attribution) {
    return [{ name: "Attribution", value: "Not captured", inline: true }];
  }

  return SIGNUP_ATTRIBUTION_FIELDS.flatMap((field) => {
    const value = attribution[field.key];
    if (!value) return [];
    return [{ name: field.slackName, value, inline: field.inline }];
  });
}

function getReferrerHost(referrer: string | undefined): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch {
    return null;
  }
}

function isGoogleHost(host: string | null): boolean {
  return Boolean(host && (host === "google.com" || host.startsWith("google.")));
}

function isPaidSearchMedium(medium: string | undefined): boolean {
  if (!medium) return false;
  return /^(cpc|ppc|paid|paid_search|paid-search|sem)$/i.test(medium);
}

function buildTrafficSourceSummary(attribution: SignupAttribution): string | null {
  if (attribution.msclkid) {
    return "Microsoft Ads / paid search (click ID captured)";
  }

  const hasGoogleClickId = Boolean(
    attribution.gclid ||
    attribution.gbraid ||
    attribution.wbraid ||
    attribution.gadCampaignId,
  );
  const source = attribution.utmSource;
  const medium = attribution.utmMedium;
  const referrerHost = getReferrerHost(attribution.referrer);
  const googleSource = source?.toLowerCase() === "google" || isGoogleHost(referrerHost);

  if (hasGoogleClickId) {
    return "Google Ads / paid search (click ID captured)";
  }

  if (googleSource && isPaidSearchMedium(medium)) {
    return "Google Ads / paid search (UTM captured)";
  }

  if (isGoogleHost(referrerHost)) {
    return "Google search/referral (paid vs organic unknown; no gclid/gbraid/wbraid captured)";
  }

  if (source && medium) return `${source} / ${medium}`;
  if (source) return source;
  if (attribution.ref) return `Referral code ${attribution.ref}`;
  if (referrerHost) return `${referrerHost} referral`;

  return null;
}

export function buildSignupAttributionSlackText(input: unknown): string {
  const attribution = normalizeSignupAttribution(input);
  if (!attribution) return "Attribution: Not captured";
  const lastAttribution = normalizeLastSignupAttribution(input);

  const trafficSource = buildTrafficSourceSummary(attribution);
  const sourceLines = trafficSource ? [`Traffic source: ${trafficSource}`] : [];
  const lines = SIGNUP_ATTRIBUTION_FIELDS.flatMap((field) => {
    const value = attribution[field.key];
    if (!value) return [];
    const label = "queryParam" in field ? field.queryParam : field.slackName;
    return [`${label}: ${value}`];
  });

  const lastLines = lastAttribution
    ? SIGNUP_ATTRIBUTION_FIELDS.flatMap((field) => {
        const value = lastAttribution[field.key];
        if (!value) return [];
        const label = "queryParam" in field ? field.queryParam : field.slackName;
        return [`${label}: ${value}`];
      })
    : [];

  return [
    "Attribution:",
    ...sourceLines,
    ...lines,
    ...(lastLines.length > 0 ? ["Last touch:", ...lastLines] : []),
  ].join("\n");
}
